// src/components/product/BundleEditor/BundleEditor.jsx
import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getShops, getShopAssignedProducts } from '../../../services/api/shops';
import BundleComponentPicker from './BundleComponentPicker';
import BundleShopPricing from './BundleShopPricing';
import BundleBulkPanel from './BundleBulkPanel';
import BundleSkuFields from './BundleSkuFields';
import {
  addComponent,
  removeComponents,
  setComponentQty,
  setComponentPrice,
  prefillShop,
  applyBulk,
  loadShopPriceMap,
} from './bundleUtils';
import styles from './BundleEditor.module.css';

/**
 * Shared Bundle tab for Create/Edit Product.
 * State lives in the page's useFormData; every change goes through onChange({ bundleItems, bundleShopIds }).
 */
const BundleEditor = ({
  bundleItems,
  bundleShopIds,
  shops,
  bundleProductId,
  errors = {},
  onChange,
  formData,
  skuValidation,
  handleInputChange,
}) => {
  const { t } = useLocalization();
  const [priceMaps, setPriceMaps] = useState({});
  const [loadingMaps, setLoadingMaps] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [defaultShop, setDefaultShop] = useState(null);

  useEffect(() => {
    getShops({ assigned: true })
      .then(res => setDefaultShop((res || []).find(s => s.isDefault) || null))
      .catch(() => setDefaultShop(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingMaps(true);
      const entries = await Promise.all(shops.map(async shop => {
        try {
          return [shop.id, await loadShopPriceMap(getShopAssignedProducts, shop.id)];
        } catch {
          return [shop.id, null];
        }
      }));
      if (!cancelled) {
        setPriceMaps(Object.fromEntries(entries.filter(([, map]) => map)));
        setLoadingMaps(false);
      }
    };
    if (shops.length > 0) load();
    return () => { cancelled = true; };
  }, [shops]);

  const update = (items, shopIds = bundleShopIds) => onChange({ bundleItems: items, bundleShopIds: shopIds });

  const handleRemove = (keys) => {
    setSelectedKeys(prev => prev.filter(k => !keys.includes(k)));
    update(removeComponents(bundleItems, keys));
  };

  const handleToggleShop = (shopId) => {
    const enabled = bundleShopIds.some(id => String(id) === String(shopId));
    if (enabled) {
      update(bundleItems, bundleShopIds.filter(id => String(id) !== String(shopId)));
    } else {
      update(prefillShop(bundleItems, shopId, priceMaps[shopId]), [...bundleShopIds, shopId]);
    }
  };

  const handleBulkApply = ({ shopIds, action, value, sourceShopId }) => {
    update(applyBulk(bundleItems, { componentKeys: selectedKeys, shopIds, action, value, sourceShopId, priceMaps }));
    setShowBulk(false);
  };

  return (
    <>
    {formData && handleInputChange && (
      <BundleSkuFields
        formData={formData}
        errors={errors}
        skuValidation={skuValidation}
        handleInputChange={handleInputChange}
      />
    )}
    <div className={styles.formCard}>
      <div className={styles.cardHeader}>
        <h2>{t('products.bundleTab')}</h2>
        <p>{t('products.bundleComponents')}</p>
      </div>
      <div className={styles.cardContent}>
        <BundleComponentPicker
          bundleItems={bundleItems}
          bundleProductId={bundleProductId}
          priceMap={defaultShop ? priceMaps[defaultShop.id] : null}
          priceShopName={defaultShop?.name}
          selectedKeys={selectedKeys}
          onAdd={component => update(addComponent(bundleItems, component))}
          onRemove={handleRemove}
          onQtyChange={(key, qty) => update(setComponentQty(bundleItems, key, qty))}
          onToggleSelect={key => setSelectedKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))}
          onToggleSelectAll={() => setSelectedKeys(prev => (prev.length === bundleItems.length ? [] : bundleItems.map(i => i.key)))}
        />
        {errors.bundleItems && <span className={styles.errorText}>{errors.bundleItems}</span>}

        {selectedKeys.length > 0 && bundleShopIds.length > 0 && (
          <div className={styles.variationActions}>
            <span className={styles.selectionCount}>{t('products.selectedCount', { count: selectedKeys.length })}</span>
            <button type="button" className={styles.bulkActionsButton} onClick={() => setShowBulk(s => !s)}>
              <Settings size={14} />
              {t('products.bulkActions')}
            </button>
          </div>
        )}

        {showBulk && selectedKeys.length > 0 && (
          <BundleBulkPanel
            selectedCount={selectedKeys.length}
            shops={shops}
            bundleShopIds={bundleShopIds}
            onApply={handleBulkApply}
            onCancel={() => setShowBulk(false)}
          />
        )}

        <BundleShopPricing
          bundleItems={bundleItems}
          bundleShopIds={bundleShopIds}
          shops={shops}
          priceMaps={priceMaps}
          loadingMaps={loadingMaps}
          onToggleShop={handleToggleShop}
          onPriceChange={(key, shopId, field, value) => update(setComponentPrice(bundleItems, key, shopId, field, value))}
        />
        {errors.bundleShops && <span className={styles.errorText}>{errors.bundleShops}</span>}
      </div>
    </div>
    </>
  );
};

export default BundleEditor;
